import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Image } from '~/components/wrappers/Image';
import { shopifySrcSet } from '~/common/utils/image';
import { ProductMedia } from '~/types/product';
import CarouselArrow from 'public/assets/icons/carousel_arrow_left.svg';

interface MobileImageCarouselProps {
  media: ProductMedia[];
  productTitle: string;
  currentIndex: number;
  emblaRef: (node: HTMLDivElement | null) => void;
  onPrev: () => void;
  onNext: () => void;
  onDotClick: (index: number) => void;
}

interface MobileMediaSlideProps {
  media: ProductMedia;
  index: number;
  currentIndex: number;
  productTitle: string;
  isFirst: boolean;
}

const MobileMediaSlide: FC<MobileMediaSlideProps> = ({
  media,
  index,
  currentIndex,
  productTitle,
  isFirst,
}) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldMountVideo, setShouldMountVideo] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  useEffect(() => {
    if (media.kind !== 'video') return;
    const el = slotRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMountVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [media.kind]);

  useEffect(() => {
    if (media.kind !== 'video') return;
    if (currentIndex !== index && userPaused) {
      setUserPaused(false);
    }
  }, [currentIndex, index, media.kind, userPaused]);

  useEffect(() => {
    if (media.kind !== 'video') return;
    const video = videoRef.current;
    if (!video) return;
    if (currentIndex === index && !userPaused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [currentIndex, index, media.kind, shouldMountVideo, userPaused]);

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setUserPaused(false);
    } else {
      video.pause();
      setUserPaused(true);
    }
  };

  if (media.kind === 'image') {
    return (
      <MobileSlideImage
        src={`${media.src}&width=800&format=auto&quality=100`}
        srcSet={shopifySrcSet(media.src)}
        sizes="100vw"
        alt={media.altText ?? `${productTitle} - Product image ${index + 1}`}
        loading={isFirst ? 'eager' : 'lazy'}
      />
    );
  }

  const posterUrl = media.previewImage;
  const posterSrc = posterUrl
    ? `${posterUrl}&width=800&format=auto&quality=100`
    : undefined;

  return (
    <VideoSlot ref={slotRef}>
      {!shouldMountVideo && posterSrc && posterUrl && (
        <MobileSlideImage
          src={posterSrc}
          srcSet={shopifySrcSet(posterUrl)}
          sizes="100vw"
          alt={media.altText ?? `${productTitle} - Product video ${index + 1}`}
          loading={isFirst ? 'eager' : 'lazy'}
        />
      )}
      {shouldMountVideo && (
        <MobileSlideVideo
          ref={videoRef}
          playsInline
          muted
          loop
          preload="metadata"
          poster={posterSrc}
          onClick={handleVideoClick}
          aria-label={
            media.altText ?? `${productTitle} - Product video ${index + 1}`
          }
        >
          {media.sources.map((source) => (
            <source key={source.url} src={source.url} type={source.mimeType} />
          ))}
        </MobileSlideVideo>
      )}
    </VideoSlot>
  );
};

export const MobileImageCarousel: FC<MobileImageCarouselProps> = ({
  media,
  productTitle,
  currentIndex,
  emblaRef,
  onPrev,
  onNext,
  onDotClick,
}) => {
  return (
    <MobileGallery>
      <MobileCarouselWrapper>
        <MobileNavArrow $left onClick={onPrev} aria-label="Previous image">
          <ArrowIcon
            src={CarouselArrow}
            alt="Navigate to previous product image"
            loading="lazy"
          />
        </MobileNavArrow>
        <MobileCarouselViewport ref={emblaRef}>
          <MobileCarouselContainer>
            {media.map((item, index) => (
              <MobileCarouselSlide key={item.id}>
                <MobileMediaSlide
                  media={item}
                  index={index}
                  currentIndex={currentIndex}
                  productTitle={productTitle}
                  isFirst={index === 0}
                />
              </MobileCarouselSlide>
            ))}
          </MobileCarouselContainer>
        </MobileCarouselViewport>
        <MobileNavArrow onClick={onNext} aria-label="Next image">
          <ArrowIcon
            $rotate
            src={CarouselArrow}
            alt="Navigate to next product image"
            loading="lazy"
          />
        </MobileNavArrow>
        <MobileDots>
          {media.map((item, index) => (
            <Dot
              key={item.id}
              $active={index === currentIndex}
              onClick={() => onDotClick(index)}
            />
          ))}
        </MobileDots>
      </MobileCarouselWrapper>
    </MobileGallery>
  );
};

const MobileGallery = styled.div`
  display: none;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: flex;
    flex-direction: column;
    width: 100%;
    aspect-ratio: 3 / 4;
  }
`;

const MobileCarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MobileCarouselViewport = styled.div`
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const MobileCarouselContainer = styled.div`
  display: flex;
  height: 100%;
`;

const MobileCarouselSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const MobileSlideImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const VideoSlot = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileSlideVideo = styled.video`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  cursor: pointer;
`;

const MobileNavArrow = styled.button<{ $left?: boolean }>`
  position: absolute;
  ${({ $left }) => ($left ? 'left: 1rem;' : 'right: 1rem;')}
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1;
`;

const ArrowIcon = styled(Image)<{ $rotate?: boolean }>`
  width: 1.25rem;
  height: 1.25rem;
  transform: ${({ $rotate }) => ($rotate ? 'rotate(180deg)' : 'none')};
`;

const MobileDots = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  z-index: 2;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.white : theme.colors.greyDisabled2};
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
`;
